/**
 * Anzeige-Registry der Tastenkürzel für Auswahl/Bearbeitung der Notenvorschau.
 *
 * Dies ist die **Single Source für die Übersicht** (KeyboardShortcutsDialog.vue). Die
 * eigentlichen Handler leben weiterhin als `onKeyStroke`-Prädikate in `ScoreEditToolbar.vue`
 * (Edit/Selektion) bzw. `MusicXmlPreview.vue` (Undo/Redo, Wiedergabe). Wird dort ein Kürzel
 * geändert/ergänzt, **hier nachziehen**.
 *
 * Wichtig: Die Zuordnung spiegelt das **reale** Tastenverhalten wider – insbesondere
 * `Strg+V = Ersetzen` (`pasteNoteReplace`) und `Strg+Umschalt+V = Einfügen` (`pasteSelection`).
 */

export interface ShortcutItem {
    /**
     * Tasten-Kombinationen. Jede innere Liste ist **eine** Kombination aus Tokens
     * (im Template mit „+" verbunden); mehrere Listen sind **Alternativen** (mit „/" verbunden),
     * z. B. `[['←'], ['→']]` oder `[['Entf'], ['Rücktaste']]`.
     */
    combos: string[][];
    description: string;
    /** Optionaler Hinweis, z. B. „nur mehrteilig". */
    note?: string;
}

export interface ShortcutGroup {
    title: string;
    /** Iconify-Name (FontAwesome 6 Solid, `fa6-solid:*`). */
    icon: string;
    items: ShortcutItem[];
}

export const SHORTCUT_GROUPS: ShortcutGroup[] = [
    {
        title: 'Maus-Auswahl',
        icon: 'fa6-solid:computer-mouse',
        items: [
            {combos: [['Klick']], description: 'Note auswählen'},
            {combos: [['Strg', 'Klick']], description: 'Zur Auswahl hinzufügen / entfernen'},
            {combos: [['Umschalt', 'Klick']], description: 'Bereich auswählen'},
            {
                combos: [['Strg', 'Klick in freien Taktbereich']],
                description: 'Ganzen Takt zur Auswahl hinzufügen',
                note: 'ohne Modifier passiert im freien Taktbereich nichts',
            },
            {combos: [['Umschalt', 'Klick in freien Taktbereich']], description: 'Takt-Bereich auswählen'},
            {
                combos: [['Doppelklick in freien Taktbereich']],
                description: 'Takt-Aktionsmenü öffnen',
                note: 'nahe der Taktkante: direkt den Editor „Wiederholungen & Taktstriche" mit der getroffenen Seite',
            },
        ],
    },
    {
        title: 'Navigation',
        icon: 'fa6-solid:arrows-left-right',
        items: [
            {combos: [['←'], ['→']], description: 'Vorige / nächste Note'},
            {combos: [['Esc']], description: 'Auswahl aufheben'},
        ],
    },
    {
        title: 'Auswahl erweitern',
        icon: 'fa6-solid:object-group',
        items: [
            {combos: [['Strg', '←'], ['Strg', '→']], description: 'Note davor / danach hinzufügen'},
            {
                combos: [['Strg', 'Umschalt', '←'], ['Strg', 'Umschalt', '→']],
                description: 'Takt davor / danach hinzufügen'
            },
            {
                combos: [['Strg', 'Umschalt', '↑'], ['Strg', 'Umschalt', '↓']],
                description: 'Part darüber / darunter hinzufügen',
                note: 'nur mehrteilig',
            },
        ],
    },
    {
        title: 'Bearbeiten',
        icon: 'fa6-solid:pen',
        items: [
            {combos: [['↑'], ['↓']], description: 'Transponieren: Halbton höher / tiefer'},
            {combos: [['Umschalt', '↑'], ['Umschalt', '↓']], description: 'Transponieren: Oktave höher / tiefer'},
            {
                combos: [['Alt', '↑'], ['Alt', '↓']],
                description: 'Diatonisch: Ton (gewähltes Intervall) oberhalb / unterhalb hinzufügen',
                note: 'Intervall in der Toolbar-Gruppe „Diatonisch"'
            },
            {combos: [['Strg', 'C']], description: 'Kopieren'},
            {combos: [['Strg', 'V']], description: 'Ersetzen (Auswahl durch Kopie)'},
            {combos: [['Strg', 'Umschalt', 'V']], description: 'Einfügen (Kopie nach Auswahl)'},
            {combos: [['Strg', 'B']], description: 'Bindebogen (Tie / Slur) zur nächsten Note'},
            {
                combos: [['Strg', 'Umschalt', 'B']],
                description: 'Bass-Notation bearbeiten',
                note: 'nur Griffschrift-Ausgabe'
            },
            {
                combos: [['Strg', 'L']], description: 'Lyrics eingeben (Strophe 1/2)',
                note: 'Leertaste = nächste Note · − = Silbentrennung · _ = Melisma · Umschalt+Leertaste = zurück'
            },
            {combos: [['Strg', 'Umschalt', 'S']], description: 'Note / Pause teilen'},
            {combos: [['Strg', 'Umschalt', 'M']], description: 'Note / Pause mit der nächsten zusammenführen'},
            {
                combos: [['Strg', 'Umschalt', 'H']],
                description: 'Haltebögen der Auswahl zusammenfassen',
                note: 'gleiche Tonhöhe im selben Takt; nur Eingabe-Vorschau',
            },
            {
                combos: [['Q'], ['W']],
                description: 'Länge halbieren / verdoppeln',
                note: 'Folgenoten der Stimme rücken nach, auch über Taktlinien (gebunden)',
            },
            {combos: [['Entf'], ['Rücktaste']], description: 'Löschen', note: 'Eingabe: fragt „Pause oder nachrücken?"'},
            {combos: [['Umschalt', 'Entf']], description: 'Löschen, Folgenoten nachrücken', note: 'nur Eingabe'},
        ],
    },
    {
        title: 'Verlauf & Wiedergabe',
        icon: 'fa6-solid:clock-rotate-left',
        items: [
            {combos: [['Strg', 'Z']], description: 'Rückgängig'},
            {combos: [['Strg', 'Y']], description: 'Wiederholen'},
            {combos: [['Leertaste']], description: 'Wiedergabe / Pause'},
        ],
    },
];
